import {IPsoAgreementsViewModel} from "./pso-agreements.view-model.interface";
import {BookingSessionStorageKeys} from "../../../storage/booking-session-storage-keys";
import {BookingModel} from "../../../booking.model";
import {makeObservable, observable, runInAction} from "mobx";
import {IPsoAgreementCommonFormFields, IPsoAgreementViewModel} from "./pso-agreement.view-mode.interface";
import {IFormField} from "../../../../../../models/forms/form-field.interface";
import {
    PsoDeclarationJourneyDetailsRequest
} from "../../../../../airline-webapi/requests/download-pso-declaration.request";
import {IServiceFactory} from "../../../../../service-factory.interface";
import {AirlineWebapiResponse} from "../../../../../airline-webapi/airline-webapi-response";
import {IDownloadBlobResponse} from "../../../../../airline-webapi/responses/download-blob.response";
import {BookingFlowTypeEnum} from "../../../../booking-strategies/booking-flow-type.enum";


export abstract class PsoAgreementsModel<TFields extends IPsoAgreementCommonFormFields,
                                         TAgreement extends IPsoAgreementViewModel<TFields>> implements IPsoAgreementsViewModel<TFields, TAgreement> {
    constructor(protected readonly booking: BookingModel) {
        setTimeout(() => {
            runInAction(() => {
                this._confirmed = this.booking.storage.getItem(this.confirmedStorageKey) === "true";
                this._downloaded = this.booking.storage.getItem(this.downloadedStorageKey) === "true";
                this._submitted = this.booking.storage.getItem(this.submittedStorageKey) === "true";
            })

        })


        makeObservable<this, '_confirmed' | '_downloaded' | '_submitted'>(this, {
            _confirmed: observable.ref,
            _downloaded: observable.ref,
            _submitted: observable.ref
        })
    }

    abstract get confirmedStorageKey(): BookingSessionStorageKeys;
    abstract get downloadedStorageKey(): BookingSessionStorageKeys;
    abstract get submittedStorageKey(): BookingSessionStorageKeys;
    protected abstract getAgreements(): TAgreement[];
    protected abstract downloadSpecificDeclaration(journeyDetails: PsoDeclarationJourneyDetailsRequest): Promise<AirlineWebapiResponse<IDownloadBlobResponse>>;


    protected get services(): IServiceFactory {
        return this.booking.services;
    }

    getMarketNameTranslation(): string {
        return this.booking.pso.psoMarket?.translations.getMarketNameTranslation() ?? ""
    }

    get agreements(): TAgreement[] {
        if(this.booking.bookingStrategyAdapter.bookingFlowType !== BookingFlowTypeEnum.Booking) {
            return [];
        }
        if(!this.booking.pso.isPsoAcceptedByUser) {
            return [];
        }

        return this.getAgreements();
    }

    private _confirmed: boolean = false;
    get confirmed(): boolean {
        return this._confirmed;
    }

    set confirmed(value: boolean) {
        if(this.downloaded) {
            return;
        }
        runInAction(() => {
            this._confirmed = value;
            const date = this.booking.services.time.currentDate;
            if(value) {
                this.agreements.forEach(w => w.declarationDate = date);
            }
            this.booking.storage.setItem(this.confirmedStorageKey, value.toString() )
        });
    }

    private _downloaded: boolean = false;
    get downloaded(): boolean {
        return this._downloaded;
    }

    set downloaded(value: boolean) {
        runInAction(() => {
            this._downloaded = value;
            this.booking.storage.setItem(this.downloadedStorageKey, value.toString() )
        });

    }

    private _submitted: boolean = false;
    get submitted(): boolean {
        return this._submitted;
    }

    set submitted(value: boolean) {
        runInAction(() => {
            this._submitted = value;
            this.booking.storage.setItem(this.submittedStorageKey, value.toString() )
        });
    }

    get hasValidWorkersAgreements(): boolean {
        if(this.agreements.length === 0) {
            return true;
        }

        if(!this.confirmed || !this.downloaded || !this.submitted) {
            return false;
        }

        return this.agreements.all(w => !w.hasErrors())
    }

    activateValidationErrors(): IFormField[] {
        let fieldsWithErrors: IFormField[] = [];
        this.agreements.forEach(w => {
            fieldsWithErrors = [
                ...fieldsWithErrors,
                ...w.activateErrorsValidation({setFocusOnFirstError: false})
            ];
        });

        if(fieldsWithErrors.length > 0) {
            fieldsWithErrors[0].setFocus();
        }

        return fieldsWithErrors;
    }


    async downloadDeclaration(): Promise<void> {
        const journey = this.booking.departureJourney;
        if(!journey) {
            throw new Error("No journey found");
        }

        try {

            const journeyDetails: PsoDeclarationJourneyDetailsRequest = {
                origin: journey.designator.origin.stationCode,
                destination: journey.designator.destination.stationCode,
                departureDate: journey.designator.departureDateUtc.toISOString()
            }


            const response = await this.downloadSpecificDeclaration(journeyDetails);
            if(response.hasError) {
                this.services.alert.showError(this.services.language.translate('Could not download PSO declaration. Please try again later.'));
                return;
            }

            let fileName = response.data.fileName;

            if(!fileName) {
                if(this.agreements.length === 1) {
                    const agreement = this.agreements[0];
                    fileName = `Aeroitalia_PSO_${agreement.passengerFullName.replaceAll(' ', '_')}.pdf`
                } else {
                    fileName = 'Aeroitalia_PSO_declarations.zip';
                }
            }

            await this.services.fileSystem.saveBlob(response.data.blob, fileName);

            this.downloaded = true;

            let message: string;
            if(this.services.device.isHybrid) {
                message = this.services.language.translate('The PSO declaration has been downloaded successfully. You can find it in the Documents folder of your device.');
            } else {
                message = this.services.language.translate('The PSO declaration has been downloaded successfully. You can find it in the Downloads folder of your device.');
            }

            this.services.dialogFactory.showSimpleMessageDialog( {
                title: this.services.language.translate('PSO declaration downloaded successfully'),
                message: message,
                buttonText: this.services.language.translate('OK')
            });

        } catch(err) {
            this.services.logger.error("Failed to download PSO declaration", err);
            this.services.alert.showError(this.services.language.translate('Could not download PSO declaration. Please try again later.'));
        }

    }

}